import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ComplianceProgramCalibrationType,
  ComplianceProgramPerformanceReportRowFragment,
  GetComplianceProgramCalibrationGroupsQuery,
  GetComplianceProgramCalibrationGroupsQueryVariables,
} from "types/graphql-schema";

import { assertEdgesNonNull } from "@helpers/helpers";

import getComplianceProgramCalibrationGroups from "../graphql/get-compliance-program-calibration-groups-query";

export const USERS_NOT_IN_CALIBRATION_GROUP_VALUE = -1;
// For a picker that lists "All groups": the selected id is then null.
export const ALL_CALIBRATION_GROUPS_VALUE = 0;
export const CALIBRATION_GROUP_URL_PARAM = "calibrationGroupId";

// The `?calibrationGroupId=` value of a URL search, or null without one.
const parseCalibrationGroupParam = (search: string) => {
  const value = parseInt(
    new URLSearchParams(search).get(CALIBRATION_GROUP_URL_PARAM) ?? "",
    10,
  );
  return Number.isNaN(value) ? null : value;
};

/**
 * The calibration group picker of a review's Results: the groups, the picker
 * options, the selected group and the rows of that group. The backend already
 * returns only the groups the viewer may see, so this hook decides no permissions.
 * A `?calibrationGroupId=` in the URL selects that group.
 */
const useCalibrationGroupPicker = ({
  complianceProgramId,
  complianceProgram,
  requireGroup = false,
  pickFromUrl = false,
}: {
  complianceProgramId: number;
  // Always show one of the groups: the first one until another is picked.
  // For a person who may not see "All groups" or "Employees not in a group".
  // Without it, the hook starts on all groups.
  requireGroup?: boolean;
  // The URL is the only place that keeps the picked option: a group id,
  // USERS_NOT_IN_CALIBRATION_GROUP_VALUE or ALL_CALIBRATION_GROUPS_VALUE.
  // Without it, the hook keeps the picked option and the URL only picks the
  // first one.
  pickFromUrl?: boolean;
  complianceProgram:
    | {
        calibration: ComplianceProgramCalibrationType;
        performanceReportRows?: {
          edges: readonly ({
            node?: ComplianceProgramPerformanceReportRowFragment | null;
          } | null)[];
        } | null;
      }
    | null
    | undefined;
}) => {
  const location = useLocation();
  const urlCalibrationGroupId = useMemo(
    () => parseCalibrationGroupParam(location.search),
    [location.search],
  );
  // undefined: the person has not picked yet, so the default group shows.
  // null: the person picked all groups.
  const [selectedCalibrationGroupId, setSelectedCalibrationGroupId] = useState<
    number | null | undefined
  >(undefined);

  const userRows = useMemo(
    () =>
      complianceProgram?.performanceReportRows
        ? assertEdgesNonNull(complianceProgram.performanceReportRows)
        : [],
    [complianceProgram],
  );

  const calibrationGroupsEnabled =
    !!complianceProgram &&
    complianceProgram.calibration !== ComplianceProgramCalibrationType.Off;
  const {
    data: calibrationGroupsData,
    loading: isLoadingCalibrationGroups,
    error: calibrationGroupsError,
  } = useQuery<
    GetComplianceProgramCalibrationGroupsQuery,
    GetComplianceProgramCalibrationGroupsQueryVariables
  >(getComplianceProgramCalibrationGroups, {
    variables: {
      complianceProgramId: complianceProgramId,
    },
    skip: !calibrationGroupsEnabled,
  });
  const calibrationGroups = useMemo(() => {
    return calibrationGroupsData
      ? assertEdgesNonNull(calibrationGroupsData.calibrationGroups).map(
          (group) => ({
            ...group,
            users: compact(group.users),
            managers: compact(group.managers),
            approvers: compact(group.approvers),
          }),
        )
      : [];
  }, [calibrationGroupsData]);
  const calibrationGroupOptions = useMemo(() => {
    return [
      {
        label: "Employees not in a group",
        value: USERS_NOT_IN_CALIBRATION_GROUP_VALUE,
      },
      ...calibrationGroups.map((group) => ({
        value: group.id,
        label: group.name,
      })),
    ];
  }, [calibrationGroups]);
  const defaultCalibrationGroupId = requireGroup
    ? (calibrationGroups[0]?.id ?? null)
    : null;
  const pickedCalibrationGroupId = pickFromUrl
    ? urlCalibrationGroupId === null
      ? defaultCalibrationGroupId
      : urlCalibrationGroupId === ALL_CALIBRATION_GROUPS_VALUE
        ? null
        : urlCalibrationGroupId
    : selectedCalibrationGroupId === undefined
      ? defaultCalibrationGroupId
      : selectedCalibrationGroupId;
  const shownCalibrationGroupId =
    requireGroup &&
    !calibrationGroups.some(({ id }) => id === pickedCalibrationGroupId)
      ? defaultCalibrationGroupId
      : pickedCalibrationGroupId;
  const rowsNotInCalibrationGroup = useMemo(() => {
    const allGroupUserIds = new Set(
      calibrationGroups.flatMap((group) => group.users.map(({ id }) => id)),
    );
    return userRows.filter((row) => !allGroupUserIds.has(row.user.id));
  }, [calibrationGroups, userRows]);

  const calibrationGroupRows = useMemo(() => {
    if (shownCalibrationGroupId === null) {
      return userRows;
    }
    if (shownCalibrationGroupId === USERS_NOT_IN_CALIBRATION_GROUP_VALUE) {
      return rowsNotInCalibrationGroup;
    }
    const group = calibrationGroups.find(
      ({ id }) => id === shownCalibrationGroupId,
    );
    const groupUserIds = new Set(group ? group.users.map(({ id }) => id) : []);
    return userRows.filter((row) => groupUserIds.has(row.user.id));
  }, [
    shownCalibrationGroupId,
    calibrationGroups,
    userRows,
    rowsNotInCalibrationGroup,
  ]);

  // The groups refetch after a group is created or edited. Keep the picked
  // option while it still exists; else go back to the default group.
  useEffect(() => {
    if (pickFromUrl) {
      return;
    }
    setSelectedCalibrationGroupId((currentId) =>
      currentId === undefined ||
      currentId === null ||
      currentId === USERS_NOT_IN_CALIBRATION_GROUP_VALUE ||
      calibrationGroups.some(({ id }) => id === currentId)
        ? currentId
        : undefined,
    );
  }, [pickFromUrl, calibrationGroups]);

  // Select the URL group once per URL value, when the groups have loaded, so a
  // refetch does not select it again after the user picked another group.
  const appliedUrlCalibrationGroupIdRef = useRef<number | null | undefined>(
    undefined,
  );
  useEffect(() => {
    if (
      pickFromUrl ||
      appliedUrlCalibrationGroupIdRef.current === urlCalibrationGroupId
    ) {
      return;
    }
    if (urlCalibrationGroupId === null) {
      appliedUrlCalibrationGroupIdRef.current = null;
      setSelectedCalibrationGroupId(undefined);
      return;
    }
    if (calibrationGroups.length === 0) {
      return;
    }
    appliedUrlCalibrationGroupIdRef.current = urlCalibrationGroupId;
    setSelectedCalibrationGroupId(
      calibrationGroups.some(({ id }) => id === urlCalibrationGroupId)
        ? urlCalibrationGroupId
        : undefined,
    );
  }, [pickFromUrl, urlCalibrationGroupId, calibrationGroups]);

  return {
    calibrationGroupsEnabled,
    calibrationGroups,
    isLoadingCalibrationGroups,
    calibrationGroupsError,
    calibrationGroupOptions,
    selectedCalibrationGroupId: shownCalibrationGroupId,
    setSelectedCalibrationGroupId,
    urlCalibrationGroupId,
    // Null for a reader who is not an admin. Counted on the server from the
    // review's people: userRows skip the reader.
    usersNotInCalibrationGroupCount:
      calibrationGroupsData?.usersNotInCalibrationGroupCount ?? null,
    userRows,
    calibrationGroupRows,
  };
};

export default useCalibrationGroupPicker;
